import React, { useEffect, useState } from 'react';
import styles from './CollatoFileShareModal.module.scss';
import Modal from 'components/Modal/Modal';
import { HelpBlock } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { loadExternalScript } from 'helpers/HelperFunctions';
import { trackEvent } from 'helpers/Analytics';

const DROPBOX_EXTERNAL_SCRIPT_URL =
  'https://www.dropbox.com/static/api/2/dropins.js';
const DROPBOX_APP_KEY = 'yz4vinqw9jfanvg';

const CollatoFileShareModal = ({ onSendLink, handleExit, show }) => {
  const [link, setLink] = useState('');
  const [linkDescription, setLinkDescription] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linkDescriptionError, setLinkDescriptionError] = useState('');
  const [openingTracked, setOpeningTracked] = useState(false);

  useEffect(() => {
    setLink('');
    setLinkDescription('');
    setLinkError('');
    setLinkDescriptionError('');
    if (!openingTracked && show) {
      trackEvent('fileshare-modal-opened');
      setOpeningTracked(true);
    }
  }, [show]);

  useEffect(() => {
    if (typeof Dropbox === 'undefined') {
      // eslint-disable-line no-undef
      loadExternalScript(DROPBOX_EXTERNAL_SCRIPT_URL, [
        { name: 'id', value: 'dropboxjs' },
        { name: 'data-app-key', value: DROPBOX_APP_KEY },
      ]);
    }
  }, []);

  const validateInputs = () => {
    let isInvalid = false;
    if (link.length <= 0) {
      setLinkError('Please enter a link');
      isInvalid = true;
    } else {
      setLinkError('');
    }
    if (linkDescription.length <= 0) {
      setLinkDescriptionError('Please enter a description');
      isInvalid = true;
    } else {
      setLinkDescriptionError('');
    }
    return isInvalid;
  };

  // eslint-disable-next-line no-unused-vars
  const handleDropboxClick = () => {
    if (typeof Dropbox !== 'undefined') {
      // eslint-disable-next-line no-undef
      Dropbox.choose({
        linkType: 'direct',
        success: (files) => {
          const selectedFile = files[0];
          setLink(selectedFile.link);
          setLinkDescription(selectedFile.name);
        },
      });
    }
  };

  const getDomain = () => {
    const linkUrl = link.startsWith('http') ? link : `https://${link}`;
    try {
      return new URL(linkUrl).hostname;
    } catch (error) {
      return 'could not parse';
    }
  };

  const sendLink = () => {
    if (validateInputs()) {
      return;
    }
    const hostName = getDomain();
    trackEvent('fileshare-link-sent', { hostName });
    const formattedLinkMessage = `${linkDescription}: ${link}`;
    onSendLink(formattedLinkMessage, 'automated');
    handleExit();
  };

  return (
    <Modal closeButton onExit={handleExit} show={show}>
      <div className={styles.container}>
        <div className={styles.linkInput}>
          <label htmlFor="collato-link-input">Link</label>
          <input
            id="collato-link-input"
            type="text"
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
          {linkError && linkError.length > 0 && (
            <HelpBlock className={styles.error}>{linkError}</HelpBlock>
          )}
        </div>
        <div className={styles.descriptionInputContainer}>
          <label
            htmlFor="collato-description-input"
            className={styles.descriptionLabel}
          >
            Link description
          </label>
          <input
            id="collato-description-input"
            type="text"
            value={linkDescription}
            onChange={(e) => setLinkDescription(e.target.value)}
            placeholder="Animation project"
            className={styles.descriptionInput}
          />
          <div className={styles.buttonContainer}>
            <button onClick={sendLink} className={styles.sendButton}>
              Send
            </button>
          </div>
          {linkDescriptionError && linkDescriptionError.length > 0 && (
            <HelpBlock
              className={`${styles.error} ${styles.linkDescriptionError}`}
            >
              {linkDescriptionError}
            </HelpBlock>
          )}
        </div>
      </div>
    </Modal>
  );
};

CollatoFileShareModal.propTypes = {
  onSendLink: PropTypes.func.isRequired,
  handleExit: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
};

export default CollatoFileShareModal;
